/**
 * Jessie Premium strip cards in the Filaments mega menu.
 *
 * The header renders on every page of the site, so this is the one place in the
 * build where the colour table is NOT loaded up front: material-colours.js is
 * ~60 KB and the menu needs it for three 72x56 strips and three counts. So this
 * file — the header's only eager script, and a small one — injects
 * material-colours.js and jessie-colour.js itself, on the first pointerenter or
 * focusin on the Filaments menu item (immediately if the drawer's panel is
 * already open), and paints only then. A shopper who never opens Filaments
 * never downloads the table.
 *
 * Until that happens the strips stay `hidden` and draw nothing, but this file holds
 * their boxes open (see `reserve`): the mega menu measures its own height exactly once,
 * when it opens, so a row that grows after that measurement is clipped at the panel
 * edge. If the table never arrives, `giveUp()` releases those boxes and removes the
 * count spans with their separators: the row reads "Jessie Premium PLA / the everyday
 * filament" — a plain menu row, not a blank rectangle and not a line starting with a
 * stray "·".
 *
 * MARKUP (snippets/mega-menu-cards-item.liquid, strip-card branch)
 *   .mega-menu__cards-strip[data-jessie-strip="PLA"]
 *                                          empty, hidden; filled with one band per colour
 *   .mega-menu__cards-strip-count[data-jessie-colour-count="PLA"]
 *                                          empty, hidden; filled with "36 colors · "
 *     data-jessie-count-label / -one       the plural / singular noun (copy stays in Liquid)
 *     data-jessie-count-suffix             the separator, only when a subtitle follows
 *
 * The script tag carries the two asset URLs it has to inject, because Liquid
 * owns `asset_url` and this file cannot guess the CDN path.
 */
if (window.__jessie_menu_strip) {
  /* already loaded — the snippet renders once for the desktop menu and once for
     the drawer, so this src can be requested twice on one page */
} else {
  window.__jessie_menu_strip = true;

  (function () {
    'use strict';

    /* Scoped to the menu's own shells, deliberately.
       `[data-jessie-strip]` is not unique to the header: WP3's crosslink cards
       ("Same colors, different jobs." on the three featured pages) render the
       same attribute and are painted by assets/jessie-featured.js. A
       document-wide selector here reserved those boxes (display:flex,
       visibility:hidden) and never released them, so the crosslink strips
       painted invisibly and left an empty gutter. Both selectors therefore name
       the mega menu's own class as well as the attribute. */
    var STRIP = '.mega-menu__cards-strip[data-jessie-strip]';
    var COUNT = '.mega-menu__cards-strip-count[data-jessie-colour-count]';

    var state = 'idle'; // idle | loading | ready | failed

    /** The <script> that loaded this file, and with it the two asset URLs. */
    function tag() {
      return (
        (document.currentScript && document.currentScript.dataset.jessieColoursSrc
          ? document.currentScript
          : null) || document.querySelector('script[data-jessie-menu-strip]')
      );
    }

    var scriptTag = tag();

    function assetSrc(name) {
      if (!scriptTag) return '';
      return scriptTag.getAttribute(name) || '';
    }

    /* ------------------------------------------------------------- painting */

    /**
     * Every count span, gone. Called when the colour table is not going to
     * arrive: an empty count would leave "· the everyday filament".
     */
    function dropCounts() {
      var counts = document.querySelectorAll(COUNT);
      for (var i = 0; i < counts.length; i++) {
        if (counts[i].parentNode) counts[i].parentNode.removeChild(counts[i]);
      }
    }

    /**
     * Hold an empty strip's box open, so painting it later changes no layout.
     *
     * The mega menu measures its panel exactly once: `assets/header-menu.js` reads the
     * submenu's offsetHeight when the item is activated and freezes it into
     * `--submenu-height`, which sizes the panel's background AND its
     * `clip-path: rect(0 100% var(--submenu-height) 0)`. This script paints later — the
     * colour table is fetched on the very hover that opens the panel — so a row that is
     * 63px tall when the panel is measured and 80px tall once its strip appears is cut
     * off at the panel edge. That was the Jessie block's third row losing its meta line.
     *
     * So the empty strip keeps its 72x56 box from the start and only its *paint* is
     * deferred: `visibility: hidden` draws nothing, and the row's height — and the
     * width its title and meta wrap into — is identical before and after painting.
     *
     * Inline styles rather than a rule in the stylesheet, deliberately: the `hidden`
     * attribute must keep collapsing the strip everywhere this file has not run. No
     * script, or no colour table, means no reservation and so no blank gutter beside
     * a row that is never going to get a strip.
     */
    function reserve(el) {
      if (state === 'failed' || !el.hidden) return;
      el.style.display = 'flex';
      el.style.visibility = 'hidden';
    }

    /** Give the box back: the row collapses to a plain text row. Shrinking is safe. */
    function release(el) {
      el.style.display = '';
      el.style.visibility = '';
    }

    /**
     * The colour table is not coming. Drop the counts and release every reserved box,
     * leaving plain text rows. Rows only ever shrink here, so nothing can be pushed
     * past a panel edge that has already been measured.
     */
    function giveUp() {
      state = 'failed';
      dropCounts();
      var strips = document.querySelectorAll(STRIP);
      for (var i = 0; i < strips.length; i++) release(strips[i]);
    }

    function paintStrip(el) {
      if (el.getAttribute('data-jessie-filled') === 'true') return;
      var rows = window.JessieColour.line(el.getAttribute('data-jessie-strip'));
      /* Hue order, neutrals last: the strip reads as a spectrum, not a shuffle. */
      if (rows && window.JessieColour.displayOrder) rows = window.JessieColour.displayOrder(rows);
      if (!rows || !rows.length) {
        release(el);
        return;
      }

      var frag = document.createDocumentFragment();
      var painted = 0;
      for (var i = 0; i < rows.length; i++) {
        var hex = rows[i] && rows[i].hex;
        if (!hex) continue;
        var band = document.createElement('span');
        band.className = 'mega-menu__cards-strip-band';
        band.style.background = hex;
        frag.appendChild(band);
        painted += 1;
      }
      if (!painted) {
        release(el);
        return;
      }

      el.appendChild(frag);
      el.setAttribute('data-jessie-filled', 'true');
      el.hidden = false;
      release(el); // the box is the strip's own now, not a reservation
    }

    function paintCount(el) {
      if (el.getAttribute('data-jessie-filled') === 'true') return;
      var rows = window.JessieColour.line(el.getAttribute('data-jessie-colour-count'));
      var n = rows ? rows.length : 0;
      if (!n) {
        if (el.parentNode) el.parentNode.removeChild(el);
        return;
      }
      var one = el.getAttribute('data-jessie-count-label-one') || '';
      var many = el.getAttribute('data-jessie-count-label') || '';
      var noun = n === 1 && one ? one : many;
      var suffix = el.getAttribute('data-jessie-count-suffix') || '';
      el.textContent = n + (noun ? ' ' + noun : '') + suffix;
      el.setAttribute('data-jessie-filled', 'true');
      el.hidden = false;
    }

    /**
     * Re-settle the open desktop panel's height.
     *
     * Horizon measures a submenu exactly once — `assets/header-menu.js` writes
     * `--submenu-height` on the <header-menu> when the item is activated — and the
     * panel's background and `clip-path` are frozen at that value. This paint runs
     * later (the colour table is fetched on the same hover), so anything that grew
     * since would be clipped. `reserve` above is what keeps the rows from growing at
     * all; this is the guarantee for what a reserved strip box cannot cover — a count
     * that wraps the meta line at a narrow viewport, a web font swapping in late.
     *
     * Only ever grows, only while this strip's own panel is the open one, and never
     * from 0 (unset/closed) — otherwise a closed panel would paint itself open.
     */
    function resettle(el) {
      var host = el.closest('header-menu');
      var submenu = el.closest('.menu-list__submenu');
      var item = el.closest('.menu-list__list-item');
      if (!host || !submenu || !item) return; // mobile drawer: nothing to re-settle
      if (!item.querySelector('[aria-expanded="true"]')) return;
      var settled = parseFloat(host.style.getPropertyValue('--submenu-height'));
      if (!settled) return;
      if (submenu.offsetHeight > settled) {
        host.style.setProperty('--submenu-height', submenu.offsetHeight + 'px');
      }
    }

    function paint() {
      if (!window.JessieColour || !window.MCM_COLOURS) {
        giveUp();
        return;
      }
      state = 'ready';
      var strips = document.querySelectorAll(STRIP);
      for (var i = 0; i < strips.length; i++) paintStrip(strips[i]);
      var counts = document.querySelectorAll(COUNT);
      for (var j = 0; j < counts.length; j++) paintCount(counts[j]);
      for (var k = 0; k < strips.length; k++) resettle(strips[k]);
    }

    /* -------------------------------------------------------------- loading */

    function inject(src, done) {
      if (!src) {
        done(false);
        return;
      }
      var el = document.createElement('script');
      el.src = src;
      el.async = false;
      el.onload = function () {
        done(true);
      };
      el.onerror = function () {
        done(false);
      };
      document.head.appendChild(el);
    }

    function load() {
      if (state !== 'idle') return;
      state = 'loading';

      var afterColours = function (ok) {
        if (!ok && !window.MCM_COLOURS) {
          giveUp();
          return;
        }
        if (window.JessieColour) {
          paint();
          return;
        }
        inject(assetSrc('data-jessie-colour-src'), function (ready) {
          if (!ready && !window.JessieColour) {
            giveUp();
            return;
          }
          paint();
        });
      };

      if (window.MCM_COLOURS) {
        afterColours(true);
        return;
      }
      inject(assetSrc('data-jessie-colours-src'), afterColours);
    }

    /* --------------------------------------------------------------- wiring */

    function arm(host) {
      if (!host || host.__jessieStripArmed) return;
      host.__jessieStripArmed = true;
      host.addEventListener('pointerenter', load);
      host.addEventListener('focusin', load);
      if (host.tagName === 'DETAILS') {
        host.addEventListener('toggle', function () {
          if (host.open) load();
        });
        // The drawer can render with the panel already open.
        if (host.open) load();
      }
    }

    function init() {
      var strips = document.querySelectorAll(STRIP + ',' + COUNT);
      if (!strips.length) return;
      if (state === 'ready') {
        paint(); // a re-rendered section may have added shells
        return;
      }
      var shells = document.querySelectorAll(STRIP);
      for (var s = 0; s < shells.length; s++) reserve(shells[s]);
      for (var i = 0; i < strips.length; i++) {
        var el = strips[i];
        // Desktop: the <li> that owns the mega menu and already listens for
        // pointerenter/focus. Drawer: the <details> the summary opens.
        arm(el.closest('.menu-list__list-item'));
        arm(el.closest('details'));
        // Last resort: the block itself, so a shopper who reaches it with the
        // panel open some other way still gets a painted strip.
        arm(el.closest('.mega-menu__jessie'));
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    // The theme editor re-renders the header section on its own.
    document.addEventListener('shopify:section:load', init);
  })();
}
